import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SiteModel } from '../../../../models/site.model';
import { UnityModel } from '../../../../models/unity.model';
import { SitesService } from '../../services/sites.service';
import { UnitiesService } from '../../services/unities.service';

export interface SiteUnityFilters {
  selectedSites: string[];
  selectedUnities: string[];
}

@Component({
  selector: 'app-site-unity-filters',
  templateUrl: './site-unity-filters.component.html',
  styleUrls: ['./site-unity-filters.component.scss']
})
export class SiteUnityFiltersComponent implements OnInit, OnDestroy {
  @Input() initialFilters?: SiteUnityFilters;
  @Output() filtersChange = new EventEmitter<SiteUnityFilters>();

  private destroy$ = new Subject<void>();
  
  filtersForm!: FormGroup;
  sites: SiteModel[] = [];
  unitiesBySite: { [siteId: string]: UnityModel[] } = {};
  isLoadingUnities = false;

  constructor(
    private fb: FormBuilder,
    private sitesService: SitesService,
    private unitiesService: UnitiesService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadSites();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.filtersForm = this.fb.group({
      sites: [this.initialFilters?.selectedSites || []],
      unities: [this.initialFilters?.selectedUnities || []]
    });
  }

  private setupFormSubscriptions(): void {
    // Écouter les changements de sites pour charger les unités correspondantes
    this.filtersForm.get('sites')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedSites: string[]) => {
        if (selectedSites && selectedSites.length > 0) {
          this.loadUnitiesForSites(selectedSites);
        } else {
          this.unitiesBySite = {};
          this.filtersForm.get('unities')?.setValue([]);
        }
        this.emitFiltersChange();
      });

    // Écouter les changements d'unités
    this.filtersForm.get('unities')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFiltersChange();
      });
  }

  private loadSites(): void {
    this.sitesService.getSites()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.sites = response.data;
          
          // Si des sites sont pré-sélectionnés, charger leurs unités
          const selectedSites = this.filtersForm.get('sites')?.value;
          if (selectedSites && selectedSites.length > 0) {
            this.loadUnitiesForSites(selectedSites);
          }
        },
        error: (error) => {
          console.error('Error loading sites:', error);
        }
      });
  }

  private loadUnitiesForSites(siteIds: string[]): void {
    if (!siteIds || siteIds.length === 0) {
      return;
    }

    this.isLoadingUnities = true;
    this.unitiesService.getUnitiesBySites(siteIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Regrouper les unités par site
          this.unitiesBySite = {};
          response.data.forEach(unity => {
            if (!this.unitiesBySite[unity.siteId]) {
              this.unitiesBySite[unity.siteId] = [];
            }
            this.unitiesBySite[unity.siteId].push(unity);
          });

          // Nettoyer les unités sélectionnées qui ne font plus partie des sites sélectionnés
          this.cleanSelectedUnities(siteIds);
          this.isLoadingUnities = false;
        },
        error: (error) => {
          console.error('Error loading unities:', error);
          this.isLoadingUnities = false;
        }
      });
  }

  private cleanSelectedUnities(selectedSiteIds: string[]): void {
    const currentSelectedUnities = this.filtersForm.get('unities')?.value || [];
    const validUnityIds = new Set<string>();
    
    // Collecter tous les IDs d'unités valides pour les sites sélectionnés
    selectedSiteIds.forEach(siteId => {
      const siteUnities = this.unitiesBySite[siteId] || [];
      siteUnities.forEach(unity => validUnityIds.add(unity.id));
    });

    // Filtrer les unités sélectionnées pour ne garder que celles valides
    const cleanedUnities = currentSelectedUnities.filter((unityId: string) => 
      validUnityIds.has(unityId)
    );

    if (cleanedUnities.length !== currentSelectedUnities.length) {
      this.filtersForm.get('unities')?.setValue(cleanedUnities, { emitEvent: false });
    }
  }

  private emitFiltersChange(): void {
    const filters: SiteUnityFilters = {
      selectedSites: this.filtersForm.get('sites')?.value || [],
      selectedUnities: this.filtersForm.get('unities')?.value || []
    };
    this.filtersChange.emit(filters);
  }

  getSiteLabel(siteId: string): string {
    const site = this.sites.find(s => s.id === siteId);
    return site ? site.label : siteId;
  }

  trackBySiteId(_: number, siteId: string): string {
    return siteId;
  }

  trackByUnityId(_: number, unity: UnityModel): string {
    return unity.id;
  }

  /**
   * Méthode publique pour réinitialiser les filtres
   */
  resetFilters(): void {
    this.filtersForm.reset({
      sites: [],
      unities: []
    });
  }

  /**
   * Méthode publique pour appliquer des filtres externes
   */
  applyFilters(filters: SiteUnityFilters): void {
    this.filtersForm.patchValue({
      sites: filters.selectedSites,
      unities: filters.selectedUnities
    });
  }

  /**
   * Vérifie s'il y a des unités disponibles
   */
  hasUnities(): boolean {
    return Object.keys(this.unitiesBySite).length > 0;
  }
}